import { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { useHandleDropdownChange, useHandleTextFieldChange } from "services/forms/hooks";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { TextArea } from "ui/components/TextField/TextArea";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { EUROPEAN_COUNTRIES } from "services/api/settings";
import { AddressMap } from "ui/components/Map/AddressMap";
import { AddressData, AddressFormData } from "ui/components/Map/AddressMap/types";

import { AddressEditModalProps } from "./types";

const AddressEditModal: React.FC<AddressEditModalProps> = (props) => {
    const { show, title, formValues, onChange, onSave, onClose } = props;

    const { t } = useTranslate();
    const { createDialog } = useDialog();

    const [mapPosition, setMapPosition] = useState<{ lat: number; lng: number } | undefined>(
        formValues.latitude && formValues.longitude
            ? { lat: formValues.latitude, lng: formValues.longitude }
            : undefined
    );

    // Koordinate mogu stići nakon mounta (npr. geokodiranje adrese iz sudskog registra),
    // pa se pin mora osvježiti — useState initial vrijednost se postavlja samo jednom.
    useEffect(() => {
        if (formValues.latitude && formValues.longitude) {
            setMapPosition({ lat: formValues.latitude, lng: formValues.longitude });
        }
    }, [formValues.latitude, formValues.longitude]);

    // Wrapper za handlere koji ažuriraju parent formValues
    const setFormValues = useCallback((updater: any) => {
        if (typeof updater === "function") {
            onChange(updater(formValues));
        } else {
            onChange(updater);
        }
    }, [formValues, onChange]);

    const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
    const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);

    const handleCloseModal = () => {
        createDialog({ onConfirm: onClose });
    };

    const handleMapChange = useCallback((data: AddressData) => {
        onChange({
            ...formValues,
            country: data.country || formValues.country,
            city: data.city || formValues.city,
            state: data.state || formValues.state,
            postalCode: data.postalCode || formValues.postalCode,
            line1: data.line1 || formValues.line1,
            latitude: data.latitude,
            longitude: data.longitude,
        });

        if (data.latitude && data.longitude) {
            setMapPosition({ lat: data.latitude, lng: data.longitude });
        }
    }, [formValues, onChange]);

    const addressFormData: AddressFormData = {
        country: formValues.country ?? undefined,
        city: formValues.city ?? undefined,
        state: formValues.state ?? undefined,
        postalCode: formValues.postalCode ?? undefined,
        line1: formValues.line1 ?? undefined,
        line2: formValues.line2 ?? undefined,
    };

    return (
        <Modal show={show} onHide={onClose} position="centered" size="full">
            <ModalHeader
                icon="/media/icons/map-pin.svg"
                title={title ?? t("admin.address.edit.title")}
                onHide={onClose}
            />
            <ModalBody>
                {/*
                  `h-full` prati modal umjesto fiksnog `calc(100vh-...)`, koji je bio veći od
                  raspoloživog prostora pa se karta prelijevala preko dna. `relative` nosi
                  apsolutno pozicioniranu kartu — vidi AddressMap.
                */}
                <div className="flex h-full divide-x-2">
                    <div className="flex-1 relative">
                        <AddressMap
                            onChange={handleMapChange}
                            initialPosition={mapPosition}
                            formData={addressFormData}
                        />
                    </div>
                    <div className="xl:w-1/4 lg:w-1/4 md:w-1/3 w-full p-4">
                        <form noValidate className="text-left h-full flex flex-col">
                            <div className="flex-1 min-h-0 overflow-y-auto">
                                <div className="flex gap-3 mb-3">
                                    <div className="flex-1">
                                        <Dropdown
                                            name="country"
                                            label={t("admin.contact.address.edit.country")}
                                            placeholder={t("admin.contact.address.edit.country.placeholder")}
                                            onChange={handleDropdownChange}
                                            value={formValues.country}
                                            options={EUROPEAN_COUNTRIES}
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-3 mb-3">
                                    <div className="flex-1">
                                        <TextField
                                            type="text"
                                            name="state"
                                            label={t("admin.contact.address.edit.state")}
                                            placeholder={t("admin.contact.address.edit.state.placeholder")}
                                            onChange={handleTextFieldChange}
                                            value={formValues.state}
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-3 mb-3">
                                    <div className="flex-1">
                                        <TextField
                                            type="text"
                                            name="city"
                                            label={t("admin.contact.address.edit.city")}
                                            placeholder={t("admin.contact.address.edit.city.placeholder")}
                                            onChange={handleTextFieldChange}
                                            value={formValues.city}
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-3 mb-3">
                                    <div className="flex-1">
                                        <TextField
                                            type="text"
                                            name="postalCode"
                                            label={t("admin.contact.address.edit.postalcode")}
                                            placeholder={t("admin.contact.address.edit.postalcode.placeholder")}
                                            onChange={handleTextFieldChange}
                                            value={formValues.postalCode}
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-3 mb-3">
                                    <div className="flex-1">
                                        <TextArea
                                            name="line1"
                                            label={t("admin.contact.address.edit.line1")}
                                            placeholder={t("admin.contact.address.edit.line1.placeholder")}
                                            onChange={handleTextFieldChange}
                                            value={formValues.line1}
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-3 mb-3">
                                    <div className="flex-1">
                                        <TextArea
                                            name="line2"
                                            label={t("admin.contact.address.edit.line2")}
                                            placeholder={t("admin.contact.address.edit.line2.placeholder")}
                                            onChange={handleTextFieldChange}
                                            value={formValues.line2}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                                <Button variant="outlined" onClick={handleCloseModal}>
                                    {t("admin.close")}
                                </Button>
                                <Button variant="primary" onClick={onSave}>
                                    {t("admin.save")}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default memo(AddressEditModal);
